import { ResourceUnauthorizedError } from '../resources/core/errors';
import { AuthClient } from './client';
import { parsePythonResponse } from './utils/parse-python-response.util';

export interface ApiClient {
	get<T = unknown>(path: string, body?: unknown): Promise<T>;
	post<T = unknown>(path: string, body: unknown): Promise<T>;
	put<T = unknown>(path: string, body: unknown): Promise<T>;
	delete<T = unknown>(path: string): Promise<T>;
}

export function createApiClient(
	baseUrl: string,
	authClient: AuthClient,
	options?: { onSessionExpired?: () => void }
): ApiClient {
	async function expireSession(): Promise<never> {
		await authClient.clearTokens();
		options?.onSessionExpired?.();
		throw new Error('Session expired');
	}

	async function execute<T = unknown>(input: string, init?: RequestInit, retry = true): Promise<T> {
		const accessToken = await authClient.getAccessToken();
		const path = input.replace(/^\//, '');

		const response = await fetch(`${baseUrl}/${path}`, {
			...init,
			headers: {
				'Content-Type': 'application/json',
				...init?.headers,
				...(accessToken && {
					Authorization: `Bearer ${accessToken}`,
				}),
			},
		});

		if (response.status === 401 && retry) {
			try {
				const tokens = await authClient.refreshToken();

				return execute<T>(
					path,
					{
						...init,
						headers: {
							...init?.headers,
							Authorization: `Bearer ${tokens.accessToken}`,
						},
					},
					false
				);
			} catch {
				return expireSession();
			}
		}

		try {
			return await parsePythonResponse<T>(response);
		} catch (error) {
			if (error instanceof ResourceUnauthorizedError) {
				return expireSession();
			}

			throw error;
		}
	}

	return {
		get<T = unknown>(path: string, body?: unknown) {
			const params = new URLSearchParams();
			Object.entries(body || {}).forEach(([key, value]) => {
				params.append(key, value as string);
			});

			return execute<T>(`${path}${params.toString() ? `?${params.toString()}` : ''}`);
		},

		post<T = unknown>(path: string, body: unknown) {
			return execute<T>(path, {
				method: 'POST',
				body: JSON.stringify(body),
			});
		},

		put<T = unknown>(path: string, body: unknown) {
			return execute<T>(path, {
				method: 'PUT',
				body: JSON.stringify(body),
			});
		},

		delete<T = unknown>(path: string) {
			return execute<T>(path, {
				method: 'DELETE',
			});
		},
	};
}
